import React, { useState } from 'react'
import { Keyboard, X } from 'lucide-react'

/**
 * Keyboard Shortcuts Help Component
 * 
 * Shows a floating help button that displays available keyboard shortcuts
 * when clicked or hovered.
 */

const KeyboardShortcutsHelp = ({ shortcuts = [] }) => {
  const [isOpen, setIsOpen] = useState(false)

  // Detect platform for correct key display
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const modifierKey = isMac ? '⌘' : 'Ctrl'

  const defaultShortcuts = [
    { key: `${modifierKey} + Shift + N`, description: 'Create new note' },
    { key: `${modifierKey} + Shift + S`, description: 'Save note' },
    { key: `${modifierKey} + Shift + ←/→`, description: 'Switch note tabs' },
    { key: `${modifierKey} + Alt + ←/→`, description: 'Switch folders' },
    ...shortcuts
  ]

  return (
    <div className="fixed bottom-4 right-4 z-40">
      {/* Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="bg-indigo-600 hover:bg-indigo-700 text-white p-1 rounded-full shadow-md 
                   transition-all duration-200 hover:scale-105 
                   border border-white dark:border-gray-800"
        title="Keyboard shortcuts"
      >
        <Keyboard className="w-3 h-3" />
      </button>

      {/* Shortcuts Panel */}
      {isOpen && (
        <div 
          className="absolute bottom-16 right-0 w-72 bg-white dark:bg-gray-800 
                     border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl 
                     overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {/* Header */}
          <div className="px-4 py-3 bg-indigo-50 dark:bg-indigo-900/30 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 flex items-center gap-2">
                <Keyboard className="w-4 h-4" />
                Keyboard Shortcuts
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Shortcuts List */}
          <div className="py-2">
            {defaultShortcuts.map((shortcut, index) => (
              <div 
                key={index}
                className="px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 
                           flex items-center justify-between"
              >
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {shortcut.description}
                </span>
                <kbd className="px-2 py-1 text-xs font-mono bg-gray-100 dark:bg-gray-700 
                               text-gray-600 dark:text-gray-300 rounded border 
                               border-gray-300 dark:border-gray-600">
                  {shortcut.key}
                </kbd>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              More shortcuts coming soon!
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default KeyboardShortcutsHelp 